import {
  Box,
  Button,
  FlexBox,
  Icon,
  IconButton,
  InputText,
  Switch,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import debounce from 'lodash/debounce';
import {
  ChangeEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useBreakpointValue } from 'utils/mediaQuery';
import { useAppSelector } from 'utils/redux/useAppSelector';

import styles from './searchInput.module.scss';

type SearchInputProps = {
  onSearch: (query: string, isStrict: boolean) => void;
  onClear: VoidFunction;
  onSavedSearchClear: VoidFunction;
};

/** Represents the input component that is used for typing search queries. */
export const SearchInput = ({
  onSearch,
  onClear,
  onSavedSearchClear,
}: SearchInputProps): JSX.Element => {
  const { params, savedSearch } = useAppSelector((state) => state.search);
  const [query, setQuery] = useState(params.query ?? '');
  const [isStrict, setIsStrict] = useState(params.isStrict ?? false);

  const isSmallScreen = useBreakpointValue({ base: true, sm: false });

  useEffect(() => {
    setQuery(params.query ?? '');
    setIsStrict(params.isStrict ?? false);
  }, [params.query, params.isStrict]);

  const handleChangeQuery = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  }, []);

  const handleChangeIsStrict = useCallback(() => {
    const isStrictNext = !isStrict;

    setIsStrict(isStrictNext);
    ampli.searchStrictToggled({ value: isStrictNext });
    query && onSearch(query, isStrictNext);
  }, [onSearch, query, isStrict]);

  const debouncedOnSearch = useMemo(
    () => debounce(onSearch, 500, { leading: true }),
    [onSearch],
  );

  const handleSubmitQuery = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        debouncedOnSearch(query, isStrict);
      }
    },
    [debouncedOnSearch, query, isStrict],
  );

  const handleFocus = useCallback(
    () => ampli.searchFieldFocused({ location: 'searchPage' }),
    [],
  );

  const handleBlur = useCallback(
    () => ampli.searchFieldBlurred({ location: 'searchPage' }),
    [],
  );

  const placeholder =
    'Search conversations by ID, subject, messages, internal notes, assigned agent and more...';

  return (
    <div className={styles.searchContainer}>
      <InputText
        name="search"
        type="text"
        value={query}
        placeholder={placeholder}
        onChange={handleChangeQuery}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleSubmitQuery}
        prefix={
          savedSearch && {
            type: 'custom',
            value: (
              <FlexBox alignItems="center">
                <Text.Paragraph variant="2">
                  <span data-testid="saved-search-title">
                    {savedSearch.name}
                  </span>
                </Text.Paragraph>

                <IconButton
                  variant="tertiary"
                  icon="close"
                  onClick={onSavedSearchClear}
                />
              </FlexBox>
            ),
          }
        }
        suffix={{
          type: 'custom',
          noWrap: true,
          value: (
            <FlexBox alignItems="center">
              {query && (
                <Button
                  variant="tertiary"
                  onClick={onClear}
                  className={styles.clearButton}
                >
                  Clear
                </Button>
              )}

              <FlexBox
                alignItems="center"
                justifyContent="flex-end"
                as={isSmallScreen ? 'div' : 'label'}
                gap="200"
              >
                {!isSmallScreen && (
                  <Text.Paragraph
                    variant="3"
                    subdued
                    className={styles.exactMatchLabel}
                  >
                    Show exact matches only
                  </Text.Paragraph>
                )}

                <Tooltip
                  content={
                    <Box width="160px">
                      Use this when searching for names, emails or specific
                      phrases.
                    </Box>
                  }
                  requireClick={!!isSmallScreen}
                  side="left"
                  wrap="div"
                  wrapProps={{ style: { display: 'flex' } }}
                >
                  <Icon name="info" />
                </Tooltip>

                <Switch
                  name=""
                  value=""
                  checked={isStrict}
                  onChange={handleChangeIsStrict}
                />
              </FlexBox>
            </FlexBox>
          ),
        }}
      />
    </div>
  );
};
